import { CatalogoOption } from '../../model/catalogo/catalogo';

/**
 * Puerto: CatalogoRepository
 * 
 * Define el contrato para acceso a catálogos del sistema.
 * La implementación concreta está en infrastructure/adapters/catalogo/
 * 
 * Arquitectura Hexagonal:
 * - Presentation → inyecta este PUERTO (abstract class)
 * - Infrastructure → implementa este puerto (HttpCatalogoRepository)
 * 
 * @author Emerson Rodriguez - Ingeneo
 */
export abstract class CatalogoRepository {
  
  /**
   * Obtiene tipos de empresa desde BD
   */
  abstract getTiposEmpresa(): Promise<CatalogoOption[]>;
  
  /**
   * Obtiene tamaños de empresa desde BD
   */
  abstract getTamaniosEmpresa(): Promise<CatalogoOption[]>;

  /**
   * Obtiene tipos de cancelación de cita desde BD
   * Tabla: TPSC_TIPOS_CANC_CITAS
   * Códigos: ASE (Asesor), PRO (Programador), BEN (Empresa), BES (En Sitio), INT (Interno)
   */
  abstract getTiposCancelacionCita(): Promise<CatalogoOption[]>;

  /**
   * Obtiene justificaciones de cancelación desde BD
   * Tabla: TPSC_JUSTIFICACIONES
   * Requerido cuando el tipo de cancelador es "BES" (En Sitio) para cumplimiento tardío
   */
  abstract getJustificacionesCancelacion(): Promise<CatalogoOption[]>;

  /**
   * Obtiene motivos de cancelación desde BD
   * Tabla: TPSC_PARAMETROS_GENERALES (DSVALOR = 'stMotivo')
   * Campo obligatorio en el diálogo de cancelación de cita
   * 
   * Opciones típicas:
   * - Se cancela por novedad en servicio
   * - Novedad por el consultor
   * - Cliente no desea la atención
   * - Cliente desafiliado
   * - Novedad Cliente
   * - Consultor retirado
   */
  abstract getMotivosCancelacion(): Promise<CatalogoOption[]>;
  
  /**
   * Obtiene tipos de identificación desde BD
   * Tabla: TPSC_TIPOS_IDENTIFICACION
   * Usado para dropdowns de tipo de documento (CC, NIT, CE, etc.)
   * Legacy: ComunesFacade.listarTiposIdenti()
   */
  abstract getTiposIdentificacion(): Promise<CatalogoOption[]>;

  /**
   * Obtiene los códigos de servicio (cdservicio) que requieren Expediente y Estado de Investigación.
   * Tabla: TPSC_PARAMETROS_GENERALES (CDPARAMETRO = 'TAREAS_EXPEDIENTE')
   * Legacy: GeneralParameters.TAREAS_VALIDAS_EXPEDIENTE
   * Devuelve un array con los cdservicio separados por coma en la BD.
   */
  abstract getTareasValidasExpediente(): Promise<string[]>;

  /**
   * Obtiene los motivos de rechazo de órdenes de servicio.
   * Tabla: T094_ESTADOS (CDTIPO_ESTADO = '07', FEBAJA IS NULL)
   * Usado para dropdown de motivo de rechazo en Cambios en Orientaciones.
   * Legacy: RechazarOrdenViewImpl.java - dropdown lbMotivoRechazo
   */
  abstract getMotivosRechazoOrden(): Promise<CatalogoOption[]>;

  /**
   * Obtiene los controles de riesgo activos.
   * Tabla: TPSC_CONTROL_RIESGO (FEBAJA IS NULL)
   * Legacy: SeguimientoRiesgoPotViewImpl.java / RegistroRiesgoPotViewImpl.java - dropdown lbTipoControl
   */
  abstract getControlesRiesgo(): Promise<CatalogoOption[]>;

  /**
   * Obtiene los agentes de riesgo activos («Riesgo que le aplica» / «Riesgo identificado»).
   * Tabla: TPSC_AGENTE_RIESGO (FEBAJA IS NULL)
   * Legacy: RiesgoPotencialViewImpl.java - dropdown lbTipoRiesgo;
   *         SeguimientoRiesgoPotViewImpl.java - dropdown lbRiesgoAplica
   */
  abstract getAgentesRiesgo(): Promise<CatalogoOption[]>;

  /**
   * Obtiene los estados del módulo de alertas tempranas, en el orden funcional del legado.
   * Tabla: TPSC_ESTADOS (CDMODULO = 'riesgoPotencial', SNACTIVO = 'S', FEBAJA IS NULL)
   * Legacy: RiesgoPotencialViewImpl.java - dropdown lbEstado
   */
  abstract getEstadosRiesgoPotencial(): Promise<CatalogoOption[]>;

  /**
   * Obtiene las oficinas activas.
   * Tabla: TPSC_OFICINAS (FEBAJA IS NULL)
   * Legacy: RiesgoPotencialViewImpl.java - dropdown lbOficina
   */
  abstract getOficinas(): Promise<CatalogoOption[]>;

  /**
   * Obtiene los tipos de empleado activos.
   * Tabla: TPSC_TIPOS_EMPLEADOS (SNACTIVO = 'S')
   * Legacy: AdminEmpleadosViewImpl.java - dropdown lbTipoEmpleado (HU1259115)
   */
  abstract getTiposEmpleado(): Promise<CatalogoOption[]>;
}
